'use strict';
import Console from '../ConsoleShim.js';
import Http from 'axios';
import RadioButton from '../RadioButton.jsx';
import React from 'react';
import { Choose, If, Otherwise, When } from 'jsx-control-statements';
import { Link } from 'react-router';

/**
 * Root view for the application -- the user experience starts here.
 *
 * @export
 * @class ViewStart
 * @extends {React.Component}
 */
export default class ViewStart extends React.Component {

    constructor(props) {
        super(props);

        // Set default state
        this.state = {
            campusId: '',
            dataFetched: false,
            emails: [],
            phones: [],
            selectedContact: null,
            selectedType: 'none',
            viewState: 'none'
        };

        this.handleCampusIdChange = this.handleCampusIdChange.bind(this);
        this.handleEnterKey = this.handleEnterKey.bind(this);
        this.queryContactOptions = this.queryContactOptions.bind(this);
        this.startAuthentication = this.startAuthentication.bind(this);
    }

    /**
     * Update component state when the user types in a new value for campus ID
     *
     * @param {Object} event
     */
    handleCampusIdChange(event) {
        this.setState({campusId: event.target.value});
    }

    /**
     * Treat pressing the enter/return key the same as clicking "Go!" to query
     * available contact options for the user.
     *
     * @param {Object} event
     */
    handleEnterKey(event) {
        // Cheeck if user press return key to execute search
        event.persist();
        console.debug('Received key event on query input');
        if(event.keyCode == 13 || event.which == 13 || event.charCode == 13) {
            console.debug('Executing query on enter/return key event');
            this.queryContactOptions();
        }
    }

    /**
     * Send a query server-side to lookup the available contact options
     * for the user identified by the provided campus ID.
     */
    async queryContactOptions() {
        // Send request to fetch contact choices for the user
        try {
            let response = await Http.get(`/data/banner/contact-choices/${this.state.campusId}/${fhda.sessionId}`);

            // Update component
            this.setState({
                dataFetched: true,
                emails: response.data.emails,
                phones: response.data.phones
            });

            // Update global state
            fhda.isActiveStaff = response.data.isActiveStaff;
        }
        catch(error) {
            // Activate the error view
            // Console.log('HTTP web service /data/banner/contact-choices returned error', error);
            this.props.router.push(`/error`);
        }
    }

    /**
     * Start the authentication process server-side once the user has
     * chosen a contact method that we can use to verify their identity.
     */
    async startAuthentication() {
        try {
            if(this.state.selectedType === 'email') {
                // Start the e-mail authentication flow
                await Http.post('/flow/email/start', {
                    emailId: this.state.selectedContact.id,
                    sessionId: fhda.sessionId
                });

                // Change view state
                this.setState({viewState: 'pending-email'});
            }
            else if(this.state.selectedType === 'phone') {
                // Start the phone authentication flow
                Http.get(`/flow/twilio/start/${fhda.sessionId}/${this.state.selectedContact.id}`);

                // Activate phone authentication view
                this.props.router.push(`/auth/phone`);
            }
        }
        catch(error) {
            // Activate the error view
            // Console.log('HTTP web service /flow/twilio/start returned error', error);
            this.props.router.push(`/error`);
        }
    }

    render() {
        return <Choose>
            <When condition={this.state.viewState === 'pending-email'}>
                {this.renderEmailPending()}
            </When>
            <Otherwise>
                {this.renderStart()}
            </Otherwise>
        </Choose>;
    }

    renderEmailPending() {
        return <div>
            <div className="alert alert-success">
                Password reset e-mail sent successfully
            </div>
            <p>
                Check your e-mail inbox for a message sent by us that will have
                a secure link which will direct you back to this site to choose
                a new password.
            </p>
            <p>
                <a className="btn btn-default" href="/resetpwd">
                    <i className="fa fa-refresh"></i> Start Over
                </a>
            </p>
        </div>;
    }

    /**
     * Utility function to transform an array of e-mail options into
     * radio buttons for selecting an e-mail based authentication method.
     */
    renderEmailSelectors() {
        // Transform e-mail records into selectable radio buttons
        return this.state.emails.map(email => {
            return <RadioButton
                key={email.id}
                label={email.maskedEmail}
                value={email.id}
                checked={(this.state.selectedContact) ? this.state.selectedContact.id === email.id : false}
                handleClick={() => {
                    this.setState({
                        selectedContact: email,
                        selectedType: 'email'
                    });
                }} />;
        });
    }

    /**
     * Utility function to transform an array of phone options into
     * radio buttons for selecting a phone based authentication method.
     */
    renderPhoneSelectors() {
        // Transform phone records into selectable radio buttons
        return this.state.phones.map(phone => {
            return <RadioButton
                key={phone.id}
                label={phone.maskedPhoneNumber}
                value={phone.id}
                checked={(this.state.selectedContact) ? this.state.selectedContact.id === phone.id : false}
                handleClick={() => {
                    this.setState({
                        selectedContact: phone,
                        selectedType: 'phone'
                    });
                }} />;
        });
    }

    renderStart() {
        return <div className="row">
            <div className="col-md-12">
                <p>If you are having trouble signing into your online account with Foothill-De Anza, we recommend that you reset your password.</p>
                <p>First, please type in your 8 digit campus ID below, and click Go!</p>

                <div className="form-group">
                    <label htmlFor="inputCWID">Campus ID (CWID):</label>
                    <input name="campusId" type="text" className="form-control" value={this.state.campusId} maxLength="8" onChange={this.handleCampusIdChange} onKeyPress={this.handleEnterKey} id="inputCWID" />
                </div>

                <If condition={!this.state.dataFetched}>
                    <button className="btn btn-success" onClick={this.queryContactOptions}>
                        <i className="fa fa-magic"></i> Go!
                    </button>
                    <span>&nbsp;</span>
                    <Link to="/forgotId" className="btn btn-info">
                        <i className="fa fa-question"></i> Forgot My Campus ID
                    </Link>
                </If>

                <If condition={this.state.dataFetched}>
                    <If condition={(this.state.phones.length + this.state.emails.length) > 0}>
                        <p>
                            OK, thank you! Next, we need to verify your identity. Please select
                            a method below that we can use to contact you.
                        </p>
                    </If>

                    <If condition={this.state.phones.length > 0}>
                        <p>
                            <i className="fa fa-phone"></i> <strong>By phone ... </strong>
                        </p>
                        {this.renderPhoneSelectors()}
                    </If>

                    <If condition={this.state.emails.length > 0}>
                        <p>
                            <i className="fa fa-envelope"></i> <strong>By e-mail ... </strong>
                        </p>
                        {this.renderEmailSelectors()}
                    </If>

                    <If condition={(this.state.phones.length + this.state.emails.length) < 1}>
                        We could not find any phone or e-mail contact information for you. If you
                        are a student, you will need to visit the A&R office for your college
                        in person to have your records updated. If you are a staff member,
                        please contact your HR representative.
                    </If>

                    <button className="btn btn-success" onClick={this.startAuthentication}>
                        <i className="fa fa-magic"></i> Go!
                    </button>
                    <span>&nbsp;</span>
                    <a href="/resetpwd" className="btn btn-warning">
                        <i className="fa fa-refresh"></i> Start Over
                    </a>
                    <div id="update-contact">
                    <p>
                        If the contact information above is wrong or outdated, please contact the following:
                    </p>
                    <ul>
                        <li>If you are a student, contact the Admissions and Records office for the college you are attending</li>
                        <ul>
                            <li>Foothill College: (650) 949-7325 - <a href="mailto:webregfh@fhda.edu">webregfh@fhda.edu</a></li>
                            <li>De Anza College: (408) 864-5300 - <a href="mailto:webregda@fhda.edu">webregda@fhda.edu</a></li>
                        </ul>
                        <li>Staff and Faculty should contact the District HR for further assistance</li>
                    </ul>
                    </div>
                </If>
            </div>
        </div>;
    }
}



// WEBPACK FOOTER //
// ./src/react/views/Start.jsx